import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import {
  Progress,
  Typography,
  Space,
  theme,
  List,
} from "antd";
import {
  CloudUploadOutlined,
  FileSearchOutlined,
  RobotOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { AnalysisPhase } from "../context/BookingWizardV2Context";

const { Title, Text } = Typography;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const AnalysisContainer = styled.div<{
  $bg: string;
  $border: string;
}>`
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  padding: 40px 32px;
  background: ${({ $bg }) => $bg};
  border: 1px dashed ${({ $border }) => $border};
  min-height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const IconRing = styled.div<{
  $active: boolean;
  $primary: string;
  $primaryBg: string;
  $fillSecondary: string;
}>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin-bottom: 20px;
  color: ${({ $active, $primary }) => ($active ? "#fff" : $primary)};
  background: ${({ $active, $primary, $primaryBg, $fillSecondary }) =>
    $active ? $primary : $primaryBg || $fillSecondary};
  box-shadow: ${({ $active, $primary }) =>
    $active ? `0 4px 16px ${$primary}40` : "none"};
  animation: ${({ $active }) => ($active ? pulse : "none")} 2s ease-in-out infinite;
  transition: all 0.3s ease;
`;

const PhaseSteps = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 28px;
  flex-wrap: wrap;
  justify-content: center;
`;

const PhaseDot = styled.div<{
  $active: boolean;
  $done: boolean;
  $primary: string;
  $primaryBg: string;
  $success: string;
  $successBg: string;
  $border: string;
  $textSecondary: string;
}>`
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.3s ease;
  background: ${({ $done, $active, $successBg, $primaryBg, $border }) =>
    $done ? $successBg : $active ? $primaryBg : $border};
  color: ${({ $done, $active, $success, $primary, $textSecondary }) =>
    $done ? $success : $active ? $primary : $textSecondary};
  border: 1px solid
    ${({ $done, $active, $success, $primary, $border }) =>
      $done ? `${$success}55` : $active ? `${$primary}55` : $border};
`;

const ActivityPanel = styled.div<{
  $bg: string;
  $border: string;
}>`
  width: 100%;
  max-width: 480px;
  margin-top: 24px;
  text-align: left;
  border-radius: 8px;
  padding: 12px 16px;
  background: ${({ $bg }) => $bg};
  border: 1px solid ${({ $border }) => $border};
`;

const PHASE_ORDER: AnalysisPhase[] = [
  "uploading",
  "extracting",
  "analyzing",
  "mapping",
  "complete",
];

const ACTIVITY_KEYS = [
  { key: "secureUpload", afterPhase: "uploading" as AnalysisPhase },
  { key: "readDocument", afterPhase: "extracting" as AnalysisPhase },
  { key: "detectDocumentType", afterPhase: "analyzing" as AnalysisPhase },
  { key: "findSupplier", afterPhase: "analyzing" as AnalysisPhase },
  { key: "scanLineItems", afterPhase: "analyzing" as AnalysisPhase },
  { key: "identifyIncoterms", afterPhase: "analyzing" as AnalysisPhase },
  { key: "createProducts", afterPhase: "mapping" as AnalysisPhase },
  { key: "matchIncoterms", afterPhase: "mapping" as AnalysisPhase },
  { key: "flagMissingFields", afterPhase: "complete" as AnalysisPhase },
];

const phaseIndex = (phase: AnalysisPhase): number =>
  PHASE_ORDER.indexOf(phase);

const phaseIcon = (phase: AnalysisPhase) => {
  switch (phase) {
    case "uploading":
      return <CloudUploadOutlined />;
    case "extracting":
      return <FileSearchOutlined />;
    case "analyzing":
      return <RobotOutlined />;
    case "mapping":
      return <ThunderboltOutlined />;
    case "complete":
      return <CheckCircleOutlined />;
    default:
      return <RobotOutlined />;
  }
};

type ActivityStatus = "done" | "active" | "pending";

const getActivityStatus = (
  phase: AnalysisPhase,
  afterPhase: AnalysisPhase
): ActivityStatus => {
  const currentIdx = phaseIndex(phase);
  const afterIdx = phaseIndex(afterPhase);

  if (phase === "complete") return "done";
  if (currentIdx > afterIdx) return "done";
  if (currentIdx === afterIdx) return "active";
  return "pending";
};

interface InvoiceAnalysisAnimationProps {
  phase: AnalysisPhase;
  fileName?: string | null;
}

export const InvoiceAnalysisAnimation = ({
  phase,
  fileName,
}: InvoiceAnalysisAnimationProps) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const currentIdx = phaseIndex(phase);
  const isActive = phase !== "complete" && phase !== "idle";

  const phaseLabels: Record<string, string> = {
    uploading: t("bookingWizardV2.analysis.phases.uploading"),
    extracting: t("bookingWizardV2.analysis.phases.extracting"),
    analyzing: t("bookingWizardV2.analysis.phases.analyzing"),
    mapping: t("bookingWizardV2.analysis.phases.mapping"),
    complete: t("bookingWizardV2.analysis.phases.complete"),
  };

  const activityIcon = (status: ActivityStatus) => {
    switch (status) {
      case "done":
        return (
          <CheckCircleOutlined style={{ color: token.colorSuccess, fontSize: 14 }} />
        );
      case "active":
        return (
          <LoadingOutlined style={{ color: token.colorPrimary, fontSize: 14 }} />
        );
      default:
        return (
          <ClockCircleOutlined
            style={{ color: token.colorTextQuaternary, fontSize: 14 }}
          />
        );
    }
  };

  const showActivityList = phase !== "idle" && phase !== "error";

  return (
    <AnalysisContainer
      $bg={token.colorFillAlter}
      $border={token.colorBorder}
    >
      <IconRing
        $active={isActive}
        $primary={token.colorPrimary}
        $primaryBg={token.colorPrimaryBg}
        $fillSecondary={token.colorFillSecondary}
      >
        {phaseIcon(phase)}
      </IconRing>

      <Title level={4} style={{ margin: 0, color: token.colorTextHeading }}>
        {t(`bookingWizardV2.analysis.titles.${phase}`, {
          defaultValue: t("bookingWizardV2.analysis.titles.analyzing"),
        })}
      </Title>

      {fileName && (
        <Text type="secondary" style={{ marginTop: 8 }}>
          {fileName}
        </Text>
      )}

      <div style={{ width: "100%", maxWidth: 400, marginTop: 24 }}>
        <Progress
          percent={
            phase === "complete"
              ? 100
              : Math.min(95, ((currentIdx + 1) / PHASE_ORDER.length) * 100)
          }
          strokeColor={token.colorPrimary}
          trailColor={token.colorFillSecondary}
          showInfo={false}
          size="small"
        />
      </div>

      <PhaseSteps>
        {PHASE_ORDER.filter((p) => p !== "complete").map((p) => {
          const idx = phaseIndex(p);
          return (
            <PhaseDot
              key={p}
              $active={phase === p}
              $done={currentIdx > idx || phase === "complete"}
              $primary={token.colorPrimary}
              $primaryBg={token.colorPrimaryBg}
              $success={token.colorSuccess}
              $successBg={token.colorSuccessBg}
              $border={token.colorBorderSecondary}
              $textSecondary={token.colorTextSecondary}
            >
              {phaseLabels[p]}
            </PhaseDot>
          );
        })}
      </PhaseSteps>

      {showActivityList && (
        <ActivityPanel
          $bg={token.colorBgContainer}
          $border={token.colorBorderSecondary}
        >
          <Text
            strong
            style={{ fontSize: 12, color: token.colorTextSecondary, display: "block", marginBottom: 8 }}
          >
            {t("bookingWizardV2.analysis.activityTitle")}
          </Text>
          <List
            size="small"
            dataSource={ACTIVITY_KEYS}
            renderItem={({ key, afterPhase }) => {
              const status = getActivityStatus(phase, afterPhase);
              return (
                <List.Item style={{ padding: "4px 0", border: "none" }}>
                  <Space size={8}>
                    {activityIcon(status)}
                    <Text
                      type={status === "pending" ? "secondary" : undefined}
                      style={{
                        fontSize: 13,
                        fontWeight: status === "active" ? 500 : 400,
                      }}
                    >
                      {t(`bookingWizardV2.analysis.activities.${key}`)}
                    </Text>
                  </Space>
                </List.Item>
              );
            }}
          />
        </ActivityPanel>
      )}
    </AnalysisContainer>
  );
};
